// 13-7-2026

// SalesRequestData_Model.jsx
import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Drawer,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Pagination,
  Grid,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Popover,
  ClickAwayListener,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import DescriptionIcon from '@mui/icons-material/Description';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import InfoIcon from '@mui/icons-material/Info';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RefreshIcon from '@mui/icons-material/Refresh';
import LayersIcon from '@mui/icons-material/Layers';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import ApartmentIcon from '@mui/icons-material/Apartment';
import DomainIcon from '@mui/icons-material/Domain';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { useDispatch, useSelector } from 'react-redux';
import {
  Sales_Request_Data_Limit_Action,
  GetAll_Sales_Request_Data_Limit_Action,
} from '../../Redux/Actions/UsersAction';
import { GetDevelopersData } from '../../Redux/Actions/DevelopersAction';
import { GetallProjectsdata } from '../../Redux/Actions/Projects_Action';
import { Notify } from '../../Utility/Notify';
import baseurl, { db_url } from '../../Api/BaseUrl'; // ✅ adjust path to match your project structure

/* ══════════════════════════════════════════════════════════════════════
   Design tokens — Luxury Navy & Gold palette
   ══════════════════════════════════════════════════════════════════════ */
const COLORS = {
  navy: '#08306B',
  navyLight: '#1565C0',
  indigo: '#0D47A1',
  indigoSoft: '#fff !important',
  gold: '#1E88E5',
  goldDark: '#0D47A1',
  goldSoft: '#fff important',
  amber: '#1E88E5',
  amberSoft: '#fff !important',
  emerald: '#0F9D6B',
  emeraldSoft: '#fff !important',
  red: '#C24A4A',
  canvas: '#FFFFFF',
  card: '#FFFFFF',
  border: '#DCE6F5',
  textPrimary: '#0B2A5C',
  textSecondary: '#5679AC',
};

const FONT_HEADING = "'Playfair Display', 'Georgia', serif";
const FONT_BODY = "'DM Sans', sans-serif";

const checkIconOff = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkIconOn = <CheckBoxIcon fontSize="small" />;

/* ── Global font import (Playfair Display + DM Sans) ───────────────────── */
const LuxuryFonts = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700;800&family=DM+Sans:wght@400;500;600;700;800&display=swap');
  `}</style>
);

/* ══════════════════════════════════════════════════════════════════════
   SearchableMultiSelect — search input + checkboxes, luxury styled
   ══════════════════════════════════════════════════════════════════════ */
const SearchableMultiSelect = ({
  label,
  icon: Icon,
  options,
  selected,
  onChange,
  getId,
  getName,
  placeholder,
  disabledPlaceholder,
  accentColor = COLORS.indigo,
}) => {
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filtered = useMemo(
    () => options.filter((opt) => getName(opt).toLowerCase().includes(search.toLowerCase())),
    [options, search, getName],
  );

  const isSelected = (opt) => selected.some((s) => getId(s) === getId(opt));

  const toggle = (opt) => {
    if (isSelected(opt)) onChange(selected.filter((s) => getId(s) !== getId(opt)));
    else onChange([...selected, opt]);
  };

  const toggleAll = () => {
    if (selected.length === options.length && options.length > 0) onChange([]);
    else onChange([...options]);
  };

  const handleClose = () => {
    setOpen(false);
    setSearch('');
  };

  return (
    <Box sx={{ mb: 2.75 }}>
      <Typography
        sx={{
          fontFamily: FONT_BODY,
          fontSize: 11.5,
          fontWeight: 700,
          letterSpacing: '0.09em',
          textTransform: 'uppercase',
          color: COLORS.textSecondary,
          mb: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 0.6,
          background:'#fff !important'
        }}
      >
        {Icon && <Icon sx={{ fontSize: 15, color: accentColor }} />}
        {label}
        {selected.length > 0 && (
          <Chip
            label={selected.length}
            size="small"
            sx={{
              height: 18,
              fontSize: 10,
              fontWeight: 700,
              bgcolor: accentColor,
              color: '#fff',
              ml: 0.5,
            }}
          />
        )}
      </Typography>

      <Box
        ref={anchorRef}
        onClick={() => (options.length > 0 ? setOpen(true) : null)}
        sx={{
          border: `1.5px solid ${open ? accentColor : COLORS.border}`,
          borderRadius: '12px',
          bgcolor: options.length === 0 ? '#FAFAF7' : COLORS.card,
          px: 1.75,
          py: 1.25,
          minHeight: 46,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: options.length === 0 ? 'not-allowed' : 'pointer',
          transition: 'border-color 0.15s, box-shadow 0.15s',
          boxShadow: open ? `0 0 0 3px ${accentColor}1A` : 'none',
          '&:hover': options.length > 0 ? { borderColor: accentColor } : {},
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6, flex: 1 }}>
          {selected.length > 0 ? (
            <>
              {selected.slice(0, 3).map((item) => (
                <Chip
                  key={getId(item)}
                  label={getName(item)}
                  size="small"
                  onDelete={(e) => {
                    e.stopPropagation();
                    toggle(item);
                  }}
                  sx={{
                    height: 24,
                    fontSize: 11.5,
                    fontFamily: FONT_BODY,
                    fontWeight: 600,
                    borderRadius: '7px',
                    bgcolor: `${accentColor}15`,
                    color: accentColor,
                    '& .MuiChip-deleteIcon': { color: accentColor, fontSize: 15 },
                  }}
                />
              ))}
              {selected.length > 3 && (
                <Chip
                  label={`+${selected.length - 3}`}
                  size="small"
                  sx={{ height: 24, fontSize: 11.5, fontFamily: FONT_BODY, bgcolor: COLORS.canvas, color: COLORS.textSecondary }}
                />
              )}
            </>
          ) : (
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: 13.5, color: options.length === 0 ? '#B8B4A6' : COLORS.textSecondary }}>
              {options.length === 0 ? disabledPlaceholder || placeholder : placeholder}
            </Typography>
          )}
        </Box>
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0, marginLeft: 8 }}
        >
          <path d="M6 9L12 15L18 9" stroke={COLORS.textSecondary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </Box>

      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            width: anchorRef.current?.offsetWidth || 320,
            mt: 0.75,
            borderRadius: '14px',
            border: `1px solid ${COLORS.border}`,
            boxShadow: '0 24px 48px -12px rgba(10,14,26,0.22)',
          },
        }}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Box>
            <Box sx={{ p: 1.5, borderBottom: `1px solid ${COLORS.border}` }}>
              <TextField
                fullWidth
                size="small"
                autoFocus
                placeholder="Search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ fontSize: 18, color: COLORS.textSecondary, mr: 0.5 }} />,
                  endAdornment: search && (
                    <IconButton size="small" onClick={() => setSearch('')}>
                      <ClearIcon sx={{ fontSize: 15 }} />
                    </IconButton>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '9px',
                    bgcolor: COLORS.canvas,
                    fontSize: 13.5,
                    fontFamily: FONT_BODY,
                  },
                }}
              />
            </Box>
            <Box sx={{ px: 1.5, py: 0.75, borderBottom: `1px solid ${COLORS.border}` }}>
              <Button
                size="small"
                onClick={toggleAll}
                sx={{ textTransform: 'none', fontFamily: FONT_BODY, fontSize: 12, fontWeight: 700, color: accentColor }}
              >
                {selected.length === options.length ? 'Deselect All' : 'Select All'}
              </Button>
            </Box>
            <List sx={{ maxHeight: 260, overflowY: 'auto', p: 0.5 }}>
              {filtered.length > 0 ? (
                filtered.map((opt) => {
                  const sel = isSelected(opt);
                  return (
                    <ListItem
                      key={getId(opt)}
                      onClick={() => toggle(opt)}
                      sx={{
                        cursor: 'pointer',
                        borderRadius: '9px',
                        py: 0.75,
                        '&:hover': { bgcolor: COLORS.canvas },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <Checkbox
                          checked={sel}
                          icon={checkIconOff}
                          checkedIcon={checkIconOn}
                          size="small"
                          sx={{ p: 0, color: COLORS.textSecondary, '&.Mui-checked': { color: accentColor } }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={getName(opt)}
                        primaryTypographyProps={{ fontFamily: FONT_BODY, fontSize: 13.5, fontWeight: sel ? 600 : 400, color: COLORS.textPrimary }}
                      />
                    </ListItem>
                  );
                })
              ) : (
                <ListItem sx={{ justifyContent: 'center', py: 2.5 }}>
                  <Typography variant="body2" sx={{ fontFamily: FONT_BODY, color: COLORS.textSecondary, fontSize: 13 }}>
                    No results found
                  </Typography>
                </ListItem>
              )}
            </List>
          </Box>
        </ClickAwayListener>
      </Popover>
    </Box>
  );
};

/* ══════════════════════════════════════════════════════════════════════
   Segmented pill tab switcher
   ══════════════════════════════════════════════════════════════════════ */
const SegmentedTabs = ({ value, onChange, tabs }) => (
  <Box
    sx={{
      display: 'flex',
      gap: 0.5,
      p: 0.5,
      bgcolor: COLORS.indigoSoft,
      borderRadius: '14px',
      border: `1px solid ${COLORS.border}`,
    }}
  >
    {tabs.map((tab, idx) => {
      const active = value === idx;
      return (
        <Box
          key={tab.label}
          onClick={() => onChange(idx)}
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.6,
            py: 1,
            px: 1,
            borderRadius: '10px',
            cursor: 'pointer',
            bgcolor: active ? '#FFFFFF' : 'transparent',
            boxShadow: active ? '0 4px 14px rgba(13,71,161,0.16)' : 'none',
            transition: 'all 0.18s ease',
          }}
        >
          <tab.icon sx={{ fontSize: 17, color: active ? tab.accent : COLORS.textSecondary }} />
          <Typography
            sx={{
              fontFamily: FONT_BODY,
              fontSize: 12.5,
              fontWeight: active ? 700 : 500,
              color: active ? COLORS.indigo : COLORS.textSecondary,
              whiteSpace: 'nowrap',
            }}
          >
            {tab.label}
          </Typography>
        </Box>
      );
    })}
  </Box>
);

/* ══════════════════════════════════════════════════════════════════════
   Main Component
   ══════════════════════════════════════════════════════════════════════ */
export const SalesRequestData_Model = ({ open, onClose }) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();

  const [tabValue, setTabValue] = useState(0);
  const [leadCount, setLeadCount] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [userlogin, setUserlogin] = useState(null);
  const [userlimitrequested, setuserlimitrequested] = useState(100);

  // History states
  const [historyPage, setHistoryPage] = useState(1);
  const [historyLimit] = useState(10);
  const [statusFilter, setStatusFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);

  // Request Specific Leads states
  const [selectedDevelopers, setSelectedDevelopers] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [specificLeadCount, setSpecificLeadCount] = useState('');
  const [specificError, setSpecificError] = useState('');
  const [specificConfirmDialog, setSpecificConfirmDialog] = useState(false);
  const [isSubmittingSpecific, setIsSubmittingSpecific] = useState(false);
  const [specificSuccess, setSpecificSuccess] = useState(false);

  // local state instead of Redux for monthly remaining
  const [monthlyRemainingData, setMonthlyRemainingData] = useState(null);
  const [monthlyRemainingLoading, setMonthlyRemainingLoading] = useState(false);

  // Redux states
  const resrequest = useSelector((state) => state.Allusers?.SalesRequestData);
  const resrequesthistory = useSelector((state) => state.Allusers?.GetAll_SalesRequestData);
  const resdevelopers = useSelector((state) => state.AllDevelopers?.Developers);
  const resprojects = useSelector((state) => state.AllProjects?.Projects);

  const developersarray = Array.isArray(resdevelopers?.data) ? resdevelopers.data : [];
  const projectsarray = Array.isArray(resprojects?.data) ? resprojects.data : [];

  // load user data from localStorage
  useEffect(() => {
    if (open) {
      const user = JSON.parse(localStorage.getItem('user'));
      if (user) {
        setUserlogin(user);
        setuserlimitrequested(user?.data?.requestedleadslimit || 100);
      }
    }
  }, [open]);

  // Fetch history when tab changes
  useEffect(() => {
    if (open && tabValue === 1 && userlogin?.data?._id) {
      fetchHistory();
    }
    // eslint-disable-next-line
  }, [tabValue, historyPage, statusFilter, fromDate, toDate, userlogin]);

  // fetch monthly remaining — direct API call
  const fetchMonthlyRemaining = async () => {
    if (!userlogin?.data?._id) return;
    setMonthlyRemainingLoading(true);
    try {
      const { data } = await baseurl.get(
        `${db_url}api/v1/users/sales/monthly-remaining/${userlogin.data._id}`,
      );
      setMonthlyRemainingData(data);
    } catch (err) {
      console.error('Error fetching monthly remaining:', err);
      setMonthlyRemainingData(null);
    } finally {
      setMonthlyRemainingLoading(false);
    }
  };

  // load developers / projects / remaining when the Specific tab opens
  useEffect(() => {
    if (open && tabValue === 2) {
      dispatch(GetDevelopersData());
      dispatch(GetallProjectsdata());
      fetchMonthlyRemaining();
    }
    // eslint-disable-next-line
  }, [open, tabValue, userlogin]);

  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const queryParams = new URLSearchParams({
        userId: userlogin?.data?._id,
        page: historyPage,
        limit: historyLimit,
        sortBy: 'createdAt',
        sortOrder: 'desc',
      });

      if (statusFilter) queryParams.append('status', statusFilter);
      if (fromDate) queryParams.append('fromDate', fromDate);
      if (toDate) queryParams.append('toDate', toDate);

      await dispatch(GetAll_Sales_Request_Data_Limit_Action(queryParams.toString()));
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Monitor request response (Tab 1)
  useEffect(() => {
    if (isSubmitting === false) {
      if (resrequest?.status === 200 || resrequest?.data?.status === 'success') {
        Notify('success', 'Congratulations! Your request is approved', 'success');
        setSuccess(true);
        if (tabValue === 1) fetchHistory();
        setTimeout(() => {
          window.location.reload(false);
        }, 2000);
      } else if (resrequest?.status === 400 || resrequest?.status === 500) {
        Notify('error', 'Your request was rejected. You may have reached your limit for this month. Contact Marketing to increase your limit', '');
        setError('Request rejected due to limit exceeded');
      }
    }
    // eslint-disable-next-line
  }, [resrequest, isSubmitting]);

  const handleTabChange = (newValue) => setTabValue(newValue);

  const handleRequest = async () => {
    const count = parseInt(leadCount);
    const userLimit = userlimitrequested || 100;

    if (!leadCount || isNaN(count) || count <= 0) {
      setError('Please enter a valid number of leads');
      return;
    }
    if (count > userLimit) {
      setError(`You can only request up to ${userLimit} leads per month.`);
      return;
    }
    setError('');
    setConfirmDialog(true);
  };

  const confirmRequest = async () => {
    setConfirmDialog(false);
    setError('');
    setIsSubmitting(true);

    try {
      const requestBody = {
        userId: userlogin?.data?._id,
        requestedLimit: parseInt(leadCount),
      };
      await dispatch(Sales_Request_Data_Limit_Action(requestBody));
      setIsSubmitting(false);
    } catch (error) {
      console.error('Error submitting request:', error);
      setError(error?.message || 'Failed to submit request. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setLeadCount('');
    setSuccess(false);
    setError('');
    setConfirmDialog(false);
    setTabValue(0);
    setIsSubmitting(false);

    setSelectedDevelopers([]);
    setSelectedProjects([]);
    setSpecificLeadCount('');
    setSpecificError('');
    setSpecificConfirmDialog(false);
    setIsSubmittingSpecific(false);
    setSpecificSuccess(false);
    setMonthlyRemainingData(null);

    onClose();
  };

  const handleResetFilters = () => {
    setStatusFilter('');
    setFromDate('');
    setToDate('');
    setHistoryPage(1);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return { bg: COLORS.emeraldSoft, text: COLORS.emerald };
      case 'pending': return { bg: COLORS.goldSoft, text: COLORS.goldDark };
      case 'failed': return { bg: 'rgba(194,74,74,0.10)', text: COLORS.red };
      case 'partial': return { bg: COLORS.indigoSoft, text: COLORS.indigo };
      default: return { bg: COLORS.canvas, text: COLORS.textSecondary };
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-GB', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  // filter projects based on selected Developers
  const filteredProjectsByDeveloper = useMemo(() => {
    if (selectedDevelopers.length === 0) return projectsarray;
    const devIds = selectedDevelopers.map((d) => d._id);
    return projectsarray.filter((p) => {
      const projDevId = p?.developer?._id || p?.developer;
      return devIds.includes(projDevId);
    });
  }, [selectedDevelopers, projectsarray]);

  useEffect(() => {
    setSelectedProjects((prev) => prev.filter((p) => filteredProjectsByDeveloper.some((fp) => fp._id === p._id)));
    // eslint-disable-next-line
  }, [filteredProjectsByDeveloper]);

  const remainingThisMonth = monthlyRemainingData?.data?.remaining ?? null;
  const maxAllowedThisMonth = monthlyRemainingData?.data?.maxAllowed ?? null;
  const monthLabel = monthlyRemainingData?.data?.month ?? '';

  const handleSpecificRequest = () => {
    const count = parseInt(specificLeadCount);

    if (selectedDevelopers.length === 0 && selectedProjects.length === 0) {
      setSpecificError('Select at least one Developer or Project');
      return;
    }
    if (!specificLeadCount || isNaN(count) || count <= 0) {
      setSpecificError('Please enter a valid number of leads');
      return;
    }
    if (remainingThisMonth !== null && count > remainingThisMonth) {
      setSpecificError(`You can only request up to ${remainingThisMonth} leads this month`);
      return;
    }
    setSpecificError('');
    setSpecificConfirmDialog(true);
  };

  const confirmSpecificRequest = async () => {
    setSpecificConfirmDialog(false);
    setSpecificError('');
    setIsSubmittingSpecific(true);

    try {
      const requestBody = {
        userId: userlogin?.data?._id,
        requestedLimit: parseInt(specificLeadCount),
        // developerIds: selectedDevelopers.map((d) => d._id),
        projectIds: selectedProjects.map((p) => p._id),
      };

      const { data } = await baseurl.post(
        `${db_url}api/v1/users/sales/request-leads-from-data-by-filters`,
        requestBody,
      );

      if (data?.status === 'success') {
        Notify('success', data?.message || 'Your request was sent successfully', 'success');
        setSpecificSuccess(true);
        fetchMonthlyRemaining();
        setTimeout(() => {
          window.location.reload(false);
        }, 2000);
      } else {
        const msg = data?.message || 'Request failed, please try again';
        Notify('error', msg, '');
        setSpecificError(msg);
      }
    } catch (error) {
      const msg = error?.response?.data?.message || 'Failed to send request, please try again';
      console.error('Error submitting specific request:', error);
      Notify('error', msg, '');
      setSpecificError(msg);
    } finally {
      setIsSubmittingSpecific(false);
    }
  };

  const historyData = resrequesthistory?.data?.data || resrequesthistory?.data || [];
  const pagination = resrequesthistory?.data?.pagination || null;

  const userLimit = userlimitrequested || 100;
  const maxLimit = userlogin?.data?.requestedleadslimit || 100;

  const tabs = [
    { label: 'Request Leads', icon: RequestQuoteIcon, accent: COLORS.indigo },
    { label: 'History', icon: InfoIcon, accent: COLORS.indigo },
    { label: 'Detailed Request', icon: LayersIcon, accent: COLORS.indigo },
  ];

  return (
    <>
      <LuxuryFonts />
      <Drawer
        anchor="right"
        open={open}
        onClose={handleClose}
        PaperProps={{
        sx: {
            width: isSmallScreen ? '100%' : 760,
            maxWidth: isSmallScreen ? '100%' : 760,
            flexShrink: 0,
            boxSizing: 'border-box',
            bgcolor: COLORS.canvas,
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {/* ── Header ── */}
        <Box
          sx={{
            background: COLORS.card,
            px: 3,
            pt: 3,
            pb: 2.5,
            flexShrink: 0,
            position: 'relative',
            overflow: 'hidden',
            borderBottom: `1px solid ${COLORS.border}`,
          }}
        >
          {/* subtle blue hairline accent */}
          <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: `linear-gradient(90deg, transparent, ${COLORS.indigo}, transparent)` }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '11px',
                  bgcolor: COLORS.indigoSoft,
                  border: `1px solid ${COLORS.indigo}33`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <DescriptionIcon sx={{ fontSize: 20, color: COLORS.indigo }} />
              </Box>
              <Box>
                <Typography sx={{ fontFamily: FONT_HEADING, fontSize: 19, fontWeight: 700, letterSpacing: '0.01em', lineHeight: 1.2, color: COLORS.textPrimary }}>
                  Request Data
                </Typography>
                <Typography sx={{ fontFamily: FONT_BODY, fontSize: 12, color: COLORS.textSecondary, mt: '2px' }}>
                  Request new leads from the CRM
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={handleClose} size="small" sx={{ color: COLORS.textSecondary, '&:hover': { color: COLORS.indigo, bgcolor: COLORS.indigoSoft } }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <SegmentedTabs value={tabValue} onChange={handleTabChange} tabs={tabs} />
        </Box>

        {/* ── Body ── */}
        <Box sx={{ flex: 1, overflowY: 'auto', p: 3, fontFamily: FONT_BODY,background:'#fff !important' }}>
          {/* Tab 1: Request Leads */}
          {tabValue === 0 && (
            <>
              {success ? (
                <Box
                  sx={{
                    bgcolor: '#fff !important',
                    border: `1px solid ${COLORS.emerald}33`,
                    borderRadius: '14px',
                    p: 2.5,
                    textAlign: 'center',
                  }}
                >
                  <Typography sx={{ fontFamily: FONT_HEADING, fontSize: 16, fontWeight: 700, color: COLORS.emerald, mb: 0.5 }}>
                    ✓ Your request was sent successfully!
                  </Typography>
                  <Typography sx={{ fontFamily: FONT_BODY, fontSize: 13, color: COLORS.textSecondary }}>
                    You'll find the new leads in <strong>CRM Data</strong> shortly.
                  </Typography>
                </Box>
              ) : (
                <>
                  <Box
                    sx={{
                      bgcolor: COLORS.card,
                      border: `1px solid ${COLORS.border}`,
                      borderRadius: '14px',
                      p: 2.25,
                      mb: 2.5,
                    }}
                  >
                    <Typography sx={{ fontFamily: FONT_BODY, fontSize: 13, color: COLORS.textSecondary, lineHeight: 1.7 }}>
                      Request data from the CRM. Enter the number of leads you need and press Request —
                      they'll appear in <strong style={{ color: COLORS.textPrimary }}>CRM Data</strong>.
                    </Typography>
                  </Box>

                  {/* <Box
                    sx={{
                      bgcolor: COLORS.card,
                      border: `1px solid ${COLORS.border}`,
                      borderRadius: '14px',
                      p: 2.25,
                      mb: 2.5,
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                  > */}
                    {/* <Box sx={{ position: 'absolute', top: 0, left: 0, width: '4px', height: '100%', bgcolor: COLORS.gold }} /> */}
                    {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5 }}>
                      <TrendingUpIcon sx={{ fontSize: 16, color: COLORS.indigo }} />
                      <Typography sx={{ fontFamily: FONT_BODY, fontSize: 11.5, fontWeight: 700, letterSpacing: '0.09em', textTransform: 'uppercase', color: COLORS.textSecondary }}>
                        Monthly Limit
                      </Typography>
                    </Box> */}
                    {/* <Box sx={{ display: 'flex', gap: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontFamily: FONT_HEADING, fontSize: 24, fontWeight: 700, color: COLORS.textPrimary }}>{maxLimit}</Typography>
                        <Typography sx={{ fontFamily: FONT_BODY, fontSize: 11.5, color: COLORS.textSecondary }}>Max per month</Typography>
                      </Box>
                      <Box sx={{ width: '1px', bgcolor: COLORS.border }} /> */}
                      {/* <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontFamily: FONT_HEADING, fontSize: 24, fontWeight: 700, color: COLORS.goldDark }}>{userLimit}</Typography>
                        <Typography sx={{ fontFamily: FONT_BODY, fontSize: 11.5, color: COLORS.textSecondary }}>Remaining this month</Typography>
                      </Box> */}
                    {/* </Box> */}
                    {/* <Typography sx={{ fontFamily: FONT_BODY, fontSize: 10.5, color: '#A3A08F', mt: 1.5 }}>
                      * Limit resets at the start of each month
                    </Typography> */}
                  {/* </Box> */}

                  {error && (
                    <Alert severity="error" sx={{ mb: 2, borderRadius: '10px', fontFamily: FONT_BODY }} onClose={() => setError('')}>
                      {error}
                    </Alert>
                  )}

                  <TextField
                    fullWidth
                    label="Number of Leads"
                    type="number"
                    value={leadCount}
                    onChange={(e) => { setLeadCount(e.target.value); setError(''); }}
                    placeholder="Enter number of leads..."
                    InputProps={{ inputProps: { min: 1, max: userLimit } }}
                    helperText={`Enter a number between 1 and ${userLimit}`}
                    sx={{
                      mb: 2.5,
                      fontFamily: FONT_BODY,
                      '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: COLORS.card, fontFamily: FONT_BODY },
                      '& .MuiInputLabel-root': { fontFamily: FONT_BODY },
                    }}
                  />

                  <Box sx={{ bgcolor: COLORS.indigoSoft, borderRadius: '14px', p: 2.25, border: `1px solid ${COLORS.indigo}14` }}>
                    <Typography sx={{ fontFamily: FONT_BODY, fontSize: 12.5, fontWeight: 700, color: COLORS.indigo, mb: 1 }}>
                      ℹ️ What happens after you request?
                    </Typography>
                    {['The system processes your request instantly', 'New leads are placed into CRM Data', 'You can view them immediately after approval'].map((t, i) => (
                      <Typography key={i} sx={{ fontFamily: FONT_BODY, fontSize: 12, color: COLORS.textSecondary, display: 'block', mb: 0.4 }}>
                        {i + 1}. {t}
                      </Typography>
                    ))}
                  </Box>
                </>
              )}
            </>
          )}

          {/* Tab 2: Request History */}
          {tabValue === 1 && (
            <>
              <Box
                sx={{
                  bgcolor: COLORS.card,
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: '14px',
                  p: 2,
                  mb: 2.5,
                }}
              >
                <Grid container spacing={1.5} alignItems="center">
                  <Grid item xs={6}>
                    <FormControl fullWidth size="small">
                      <Select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        displayEmpty
                        sx={{ borderRadius: '10px', fontSize: 13, fontFamily: FONT_BODY }}
                      >
                        <MenuItem value="">All Statuses</MenuItem>
                        <MenuItem value="completed">Completed</MenuItem>
                        <MenuItem value="pending">Pending</MenuItem>
                        <MenuItem value="failed">Failed</MenuItem>
                        <MenuItem value="partial">Partial</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      variant="outlined"
                      size="small"
                      onClick={handleResetFilters}
                      startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
                      sx={{ textTransform: 'none', fontFamily: FONT_BODY, borderRadius: '10px', borderColor: COLORS.border, color: COLORS.textSecondary }}
                    >
                      Reset
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth size="small" type="date" label="From"
                      value={fromDate} onChange={(e) => setFromDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', fontFamily: FONT_BODY } }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth size="small" type="date" label="To"
                      value={toDate} onChange={(e) => setToDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', fontFamily: FONT_BODY } }}
                    />
                  </Grid>
                </Grid>
              </Box>

              {historyLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                  <CircularProgress size={28} sx={{ color: COLORS.indigo }} />
                </Box>
              ) : historyData && historyData.length > 0 ? (
                <>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                    {historyData.map((request) => {
                      const statusStyle = getStatusColor(request.status);
                      return (
                        <Box
                          key={request._id}
                          sx={{
                            bgcolor: COLORS.card,
                            border: `1px solid ${COLORS.border}`,
                            borderRadius: '12px',
                            p: 1.75,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Box>
                            <Typography sx={{ fontFamily: FONT_BODY, fontSize: 12.5, color: COLORS.textSecondary, mb: 0.3 }}>
                              {formatDate(request.createdAt)}
                            </Typography>
                            <Typography sx={{ fontFamily: FONT_BODY, fontSize: 13.5, fontWeight: 700, color: COLORS.textPrimary }}>
                              {request.actualtransferredcount || 0} / {request.requestedlimit} leads
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip
                              label={request.status}
                              size="small"
                              sx={{ bgcolor: statusStyle.bg, color: statusStyle.text, fontWeight: 700, fontSize: 11, fontFamily: FONT_BODY }}
                            />
                            <Tooltip title="Details">
                              <IconButton
                                size="small"
                                onClick={() => { setSelectedRequest(request); setDetailsDialogOpen(true); }}
                              >
                                <VisibilityIcon fontSize="small" sx={{ color: COLORS.textSecondary }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>

                  {pagination && pagination.totalPages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2.5 }}>
                      <Pagination
                        count={pagination.totalPages}
                        page={historyPage}
                        onChange={(e, page) => setHistoryPage(page)}
                        size="small"
                        sx={{ '& .Mui-selected': { bgcolor: `${COLORS.indigo} !important`, color: '#fff' } }}
                      />
                    </Box>
                  )}
                </>
              ) : (
                <Alert severity="info" sx={{ borderRadius: '10px', fontFamily: FONT_BODY }}>
                  No requests yet. Make your first request!
                </Alert>
              )}
            </>
          )}

          {/* Tab 3: Request Specific Leads */}
          {tabValue === 2 && (
            <>
              {specificSuccess ? (
                <Box
                  sx={{
                    bgcolor: COLORS.emeraldSoft,
                    border: `1px solid ${COLORS.emerald}33`,
                    borderRadius: '14px',
                    p: 2.5,
                    textAlign: 'center',
                  }}
                >
                  <Typography sx={{ fontFamily: FONT_HEADING, fontSize: 16, fontWeight: 700, color: COLORS.emerald, mb: 0.5 }}>
                    ✓ Your request was sent successfully!
                  </Typography>
                  <Typography sx={{ fontFamily: FONT_BODY, fontSize: 13, color: COLORS.textSecondary }}>
                    You'll find the new leads in <strong>CRM Data</strong> shortly.
                  </Typography>
                </Box>
              ) : (
                <>
                  <Box
                    sx={{
                      bgcolor: COLORS.goldSoft,
                      border: `1px solid ${COLORS.gold}40`,
                      borderRadius: '14px',
                      p: 2.25,
                      mb: 2.75,
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                  >
                    <Box sx={{ position: 'absolute', top: 0, left: 0, width: '4px', height: '100%', bgcolor: COLORS.gold }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                      <CalendarMonthIcon sx={{ fontSize: 16, color: COLORS.goldDark }} />
                      <Typography sx={{ fontFamily: FONT_BODY, fontSize: 11.5, fontWeight: 700, letterSpacing: '0.09em', textTransform: 'uppercase', color: COLORS.goldDark }}>
                        Remaining Limit {monthLabel && `— ${monthLabel}`}
                      </Typography>
                    </Box>
                    {monthlyRemainingLoading ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={16} sx={{ color: COLORS.goldDark }} />
                        <Typography sx={{ fontFamily: FONT_BODY, fontSize: 12.5, color: COLORS.textSecondary }}>Loading...</Typography>
                      </Box>
                    ) : remainingThisMonth === null ? (
                      <Typography sx={{ fontFamily: FONT_BODY, fontSize: 12.5, color: COLORS.textSecondary }}>
                        No data yet, try reopening this tab
                      </Typography>
                    ) : (
                      <Typography sx={{ fontFamily: FONT_HEADING, fontSize: 22, fontWeight: 700, color: COLORS.textPrimary }}>
                        {remainingThisMonth} <Typography component="span" sx={{ fontFamily: FONT_BODY, fontSize: 13, fontWeight: 500, color: COLORS.textSecondary }}>/ {maxAllowedThisMonth} leads</Typography>
                      </Typography>
                    )}
                  </Box>

                  {specificError && (
                    <Alert severity="error" sx={{ mb: 2.5, borderRadius: '10px', fontFamily: FONT_BODY }} onClose={() => setSpecificError('')}>
                      {specificError}
                    </Alert>
                  )}

                  <SearchableMultiSelect
                    label="Developers"
                    icon={ApartmentIcon}
                    options={developersarray}
                    selected={selectedDevelopers}
                    onChange={setSelectedDevelopers}
                    getId={(o) => o._id}
                    getName={(o) => o?.name || ''}
                    placeholder="Select Developer..."
                    accentColor={COLORS.indigo}
                  />

                  <SearchableMultiSelect
                    label="Projects"
                    icon={DomainIcon}
                    options={filteredProjectsByDeveloper}
                    selected={selectedProjects}
                    onChange={setSelectedProjects}
                    getId={(o) => o._id}
                    getName={(o) => o?.name || ''}
                    placeholder="Select Project..."
                    disabledPlaceholder="Select a Developer first"
                    accentColor={COLORS.goldDark}
                  />

                  <TextField
                    fullWidth
                    label="Number of Leads"
                    type="number"
                    value={specificLeadCount}
                    onChange={(e) => { setSpecificLeadCount(e.target.value); setSpecificError(''); }}
                    placeholder="Enter the number of leads you want..."
                    InputProps={{ inputProps: { min: 1, max: remainingThisMonth || undefined } }}
                    helperText={remainingThisMonth !== null ? `Maximum you can request: ${remainingThisMonth}` : ''}
                    sx={{
                      mb: 3,
                      '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: COLORS.card, fontFamily: FONT_BODY },
                      '& .MuiInputLabel-root': { fontFamily: FONT_BODY },
                    }}
                  />
                </>
              )}
            </>
          )}
        </Box>

        {/* ── Footer ── */}
        <Box
          sx={{
            borderTop: `1px solid ${COLORS.border}`,
            bgcolor: COLORS.card,
            p: 2.25,
            display: 'flex',
            gap: 1.25,
            flexShrink: 0,
          }}
        >
          <Button
            onClick={handleClose}
            variant="outlined"
            sx={{
              flex: tabValue === 1 ? 1 : 'unset',
              textTransform: 'none',
              fontFamily: FONT_BODY,
              borderRadius: '11px',
              borderColor: COLORS.border,
              color: COLORS.textSecondary,
              fontWeight: 600,
              px: 3,
            }}
          >
            Close
          </Button>

          {tabValue === 0 && !success && (
            <Button
              onClick={handleRequest}
              variant="contained"
              fullWidth
              disabled={isSubmitting === false ? false : false}
              startIcon={<SendIcon sx={{ fontSize: 17 }} />}
              sx={{
                textTransform: 'none',
                fontFamily: FONT_BODY,
                borderRadius: '11px',
                background: `linear-gradient(135deg, ${COLORS.indigo}, ${COLORS.navy})`,
                fontWeight: 700,
                letterSpacing: '0.02em',
                boxShadow: '0 6px 16px rgba(27,42,74,0.28)',
                '&:hover': { background: `linear-gradient(135deg, ${COLORS.navy}, ${COLORS.navy})`, boxShadow: '0 6px 16px rgba(27,42,74,0.35)' },
              }}
            >
              Request
            </Button>
          )}

          {tabValue === 2 && !specificSuccess && (
            <Button
              onClick={handleSpecificRequest}
              variant="contained"
              fullWidth
              disabled={isSubmittingSpecific}
              startIcon={isSubmittingSpecific ? <CircularProgress size={16} color="inherit" /> : <SendIcon sx={{ fontSize: 17 }} />}
              sx={{
                textTransform: 'none',
                fontFamily: FONT_BODY,
                borderRadius: '11px',
                background: `linear-gradient(135deg, ${COLORS.gold}, ${COLORS.goldDark})`,
                color: '#FFFFFF',
                fontWeight: 800,
                letterSpacing: '0.02em',
                boxShadow: '0 6px 16px rgba(201,162,39,0.35)',
                '&:hover': { background: `linear-gradient(135deg, ${COLORS.goldDark}, ${COLORS.goldDark})`, boxShadow: '0 6px 16px rgba(201,162,39,0.45)' },
              }}
            >
              {isSubmittingSpecific ? 'Sending...' : 'Send Request'}
            </Button>
          )}
        </Box>
      </Drawer>

      {/* Confirm Dialog - Tab 1 */}
      <Dialog open={confirmDialog} onClose={() => setConfirmDialog(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle sx={{ fontFamily: FONT_HEADING, fontWeight: 700 }}>Confirm Request</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: FONT_BODY, fontSize: 14 }}>
            Are you sure you want to request <strong>{leadCount}</strong> leads?
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: FONT_BODY, color: COLORS.textSecondary, mt: 1 }}>
            The new leads will be added to your CRM Data.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={() => setConfirmDialog(false)} sx={{ textTransform: 'none', fontFamily: FONT_BODY, color: COLORS.textSecondary }}>Cancel</Button>
          <Button
            onClick={confirmRequest}
            variant="contained"
            sx={{ textTransform: 'none', fontFamily: FONT_BODY, borderRadius: '10px', bgcolor: COLORS.indigo, fontWeight: 700, boxShadow: 'none' }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Dialog - Tab 3 */}
      <Dialog open={specificConfirmDialog} onClose={() => setSpecificConfirmDialog(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle sx={{ fontFamily: FONT_HEADING, fontWeight: 700 }}>Confirm Request</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: FONT_BODY, fontSize: 14 }}>
            Are you sure you want to request <strong>{specificLeadCount}</strong> leads?
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: FONT_BODY, color: COLORS.textSecondary, mt: 1 }}>
            Developers: {selectedDevelopers.map((d) => d.name).join(', ') || '-'}
            <br />
            Projects: {selectedProjects.map((p) => p.name).join(', ') || '-'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={() => setSpecificConfirmDialog(false)} sx={{ textTransform: 'none', fontFamily: FONT_BODY, color: COLORS.textSecondary }}>Cancel</Button>
          <Button
            onClick={confirmSpecificRequest}
            variant="contained"
            sx={{ textTransform: 'none', fontFamily: FONT_BODY, borderRadius: '10px', bgcolor: COLORS.goldDark, fontWeight: 700, boxShadow: 'none' }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Details Dialog */}
      <Dialog open={detailsDialogOpen} onClose={() => setDetailsDialogOpen(false)}  fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle sx={{ fontFamily: FONT_HEADING, fontWeight: 700 }}>Request Details</DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Box sx={{ py: 1, fontFamily: FONT_BODY }}>
              <Grid container spacing={1.5}>
                <Grid item xs={6}>
                  <Typography sx={{ fontFamily: FONT_BODY, fontSize: 11, color: COLORS.textSecondary }}>Requested</Typography>
                  <Typography sx={{ fontFamily: FONT_BODY, fontSize: 15, fontWeight: 700 }}>{selectedRequest.requestedlimit}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography sx={{ fontFamily: FONT_BODY, fontSize: 11, color: COLORS.textSecondary }}>Actually Transferred</Typography>
                  <Typography sx={{ fontFamily: FONT_BODY, fontSize: 15, fontWeight: 700 }}>{selectedRequest.actualtransferredcount || 0}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography sx={{ fontFamily: FONT_BODY, fontSize: 11, color: COLORS.textSecondary }}>Status</Typography>
                  <Chip
                    label={selectedRequest.status}
                    size="small"
                    sx={{ bgcolor: getStatusColor(selectedRequest.status).bg, color: getStatusColor(selectedRequest.status).text, fontWeight: 700, mt: 0.4, fontFamily: FONT_BODY }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography sx={{ fontFamily: FONT_BODY, fontSize: 11, color: COLORS.textSecondary }}>Date</Typography>
                  <Typography sx={{ fontFamily: FONT_BODY, fontSize: 13 }}>{formatDate(selectedRequest.createdAt)}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography sx={{ fontFamily: FONT_BODY, fontSize: 11, color: COLORS.textSecondary }}>Notes</Typography>
                  <Typography sx={{ fontFamily: FONT_BODY, fontSize: 13 }}>{selectedRequest.notes || 'None'}</Typography>
                </Grid>
              </Grid>

              {selectedRequest.leadsids && selectedRequest.leadsids.length > 0 && (
                <>
                  <Typography sx={{ fontFamily: FONT_BODY, fontSize: 12.5, fontWeight: 700, mt: 2.5, mb: 1 }}>Transferred Leads</Typography>
                  <TableContainer component={Paper} sx={{ maxHeight: 260, borderRadius: '10px', border: `1px solid ${COLORS.border}` }} elevation={0}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontFamily: FONT_BODY }}>Name</TableCell>
                          <TableCell sx={{ fontFamily: FONT_BODY }}>Phone</TableCell>
                          <TableCell sx={{ fontFamily: FONT_BODY }}>Project</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedRequest.leadsids.map((lead, idx) => (
                          <TableRow key={idx}>
                            <TableCell sx={{ fontFamily: FONT_BODY }}>{lead.name}</TableCell>
                            <TableCell sx={{ fontFamily: FONT_BODY }}>{lead.phone}</TableCell>
                            <TableCell sx={{ fontFamily: FONT_BODY }}>{lead.project?.name || '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={() => setDetailsDialogOpen(false)} sx={{ textTransform: 'none', fontFamily: FONT_BODY, color: COLORS.textSecondary }}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
